import React, { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Plus, Loader2, AlertTriangle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryState, parseAsString, createParser } from 'nuqs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SalesOrderDataTable } from './SalesOrderDataTable';
import { columns, editSalesOrderEvent, deleteSalesOrderEvent } from './columns';
import {
  listSalesOrders,
  deleteSalesOrder
} from '@/app/services/salesOrderService';
import { SalesOrderListItem } from '@/app/types/salesOrder';
import { useOrganization } from '@/app/hooks/useOrganization';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';
import { SortingState } from '@tanstack/react-table';

// Extended PaginatedResponse with page count (backend might return different field names)
interface ExtendedPaginatedResponse<T> extends PaginatedResponse<T> {
  page_count?: number;
  pageCount?: number;
  total_pages?: number;
  totalPages?: number;
}

// Define the sort direction type
type SortDirection = 'asc' | 'desc';

// Custom parser for sorting state
const sortingParser = createParser({
  parse: (value: string) => {
    if (!value) return [{ id: 'transaction_date', desc: true }]
    const [field, direction] = value.split('-')
    return [{
      id: field || 'transaction_date',
      desc: direction === 'desc'
    }]
  },
  serialize: (value: SortingState) => {
    if (!value.length) return 'transaction_date-desc'
    const sort = value[0]
    return `${sort.id}-${sort.desc ? 'desc' : 'asc'}`
  }
})

// Custom parser for page (0-based internal, 1-based in URL)
const pageParser = createParser({
  parse: (value: string) => {
    const page = parseInt(value, 10)
    return isNaN(page) || page < 1 ? 0 : page - 1 // Convert 1-based to 0-based
  },
  serialize: (value: number) => {
    return String(value + 1) // Convert 0-based to 1-based
  }
})

// Custom parser for filters
const filtersParser = createParser({
  parse: (value: string) => {
    try {
      return value ? JSON.parse(decodeURIComponent(value)) : {}
    } catch {
      return {}
    }
  },
  serialize: (value: Record<string, any>) => {
    return Object.keys(value).length > 0 ? encodeURIComponent(JSON.stringify(value)) : ''
  }
})

const SalesOrdersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useOrganization();
  const navigate = useNavigate();

  // Use nuqs for URL state management
  const [page, setPage] = useQueryState('page', pageParser.withDefault(0))
  const [search, setSearch] = useQueryState('search', parseAsString.withDefault(''))
  const [sorting, setSorting] = useQueryState('sort', sortingParser.withDefault([{ id: 'transaction_date', desc: true }]))
  const [filters, setFilters] = useQueryState('filters', filtersParser.withDefault({}))
  const [limit] = useState<number>(10);

  // Local state for dialogs
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [selectedSalesOrder, setSelectedSalesOrder] = useState<SalesOrderListItem | null>(null);

  // Determine sort parameters for API call
  const getSortParams = useCallback(() => {
    if (sorting.length === 0) return {
      sort_by: 'transaction_date',
      sort_dir: 'desc' as SortDirection
    };

    const sort = sorting[0];
    const direction: SortDirection = sort.desc ? 'desc' : 'asc';

    return {
      sort_by: sort.id,
      sort_dir: direction,
    };
  }, [sorting]);

  // Filter configuration (simplified version for sales orders)
  const buildFilterParams = (filters: Record<string, string | number | boolean | { from?: string; to?: string }>): string => {
    const filterArray: any[] = [];

    Object.entries(filters).forEach(([field, value]) => {
      if (value === undefined || value === null || value === '') return;

      // Handle date range filters (transaction_date and expected_delivery_date)
      if ((field === 'transaction_date' || field === 'expected_delivery_date') && typeof value === 'object' && 'from' in value) {
        const dateFilter = value as { from?: string; to?: string };

        if (dateFilter.from) {
          filterArray.push({
            field: field,
            value: `${dateFilter.from} 00:00:00`,
            op: 'gte'
          });
        }

        if (dateFilter.to) {
          filterArray.push({
            field: field,
            value: `${dateFilter.to} 23:59:59`,
            op: 'lte'
          });
        }
        return;
      }

      // Handle text fields with contains search
      if (field === 'party.name' || field === 'site.name' || field === 'document_number') {
        filterArray.push({
          field: field,
          value: `%${value}%`,
          op: 'ilike'
        });
        return;
      }

      // Handle exact match fields
      filterArray.push({
        field: field,
        value: String(value),
        op: 'eq'
      });
    });

    return JSON.stringify(filterArray);
  };

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Record<string, unknown>): void => {
    console.log('handleFilterChange called with:', newFilters);
    const typedFilters: Record<string, string | number | boolean | { from?: string; to?: string }> = {};

    Object.entries(newFilters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;

      // Handle date range object
      if (typeof value === 'object' && 'from' in value) {
        const dateRange = value as { from?: string; to?: string };
        if (dateRange.from || dateRange.to) {
          typedFilters[key] = dateRange;
        }
      } else if (typeof value === 'string') {
        typedFilters[key] = value;
      } else if (typeof value === 'number') {
        typedFilters[key] = value;
      } else if (typeof value === 'boolean') {
        typedFilters[key] = value;
      } else {
        typedFilters[key] = String(value);
      }
    });

    console.log('Processed filters:', typedFilters);
    setFilters(typedFilters);
    setPage(0); // Reset to first page when filters change
  }, [setFilters, setPage]);

  // Fetch sales orders with search and pagination
  const { data: salesOrdersData, isLoading, isError, error } = useQuery<ExtendedPaginatedResponse<SalesOrderListItem>>({
    queryKey: ['sales-orders', page, limit, search, sorting, filters, currentOrganizationId],
    queryFn: () => {
      const { sort_by, sort_dir } = getSortParams();
      const skip = page * limit;

      const params: ListQueryParams = {
        skip,
        limit,
        sort_by,
        sort_dir,
      };

      if (search) {
        params.search_query = search;
      }

      // Handle filters
      if (Object.keys(filters).length > 0) {
        params.filters = buildFilterParams(filters);
        console.log('Filter JSON:', params.filters);
      }

      console.log('API Query Params:', params);
      return listSalesOrders(params);
    },
    enabled: !!currentOrganizationId,
  });

  // Calculate page count from total items and limit if not directly provided
  const pageCount = salesOrdersData?.page_count ?? salesOrdersData?.pageCount ??
                   salesOrdersData?.total_pages ?? salesOrdersData?.totalPages ??
                   (salesOrdersData ? Math.ceil(salesOrdersData.total / limit) : 0);

  // Delete sales order mutation
  const deleteSalesOrderMutation = useMutation({
    mutationFn: (id: number) => deleteSalesOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      setIsDeleteAlertOpen(false);
      setSelectedSalesOrder(null);
      toast.success('Sales order deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete sales order: ${error.message}`);
    },
  });

  // Event handlers for table actions
  React.useEffect(() => {
    const handleEditSalesOrder = (event: CustomEvent<{ salesOrder: SalesOrderListItem }>): void => {
      navigate(`/sales-orders/detail/${event.detail.salesOrder.id}`);
    };

    const handleDeleteSalesOrder = (event: CustomEvent<{ salesOrder: SalesOrderListItem }>): void => {
      setSelectedSalesOrder(event.detail.salesOrder);
      setIsDeleteAlertOpen(true);
    };

    document.addEventListener(editSalesOrderEvent, handleEditSalesOrder as EventListener);
    document.addEventListener(deleteSalesOrderEvent, handleDeleteSalesOrder as EventListener);

    return () => {
      document.removeEventListener(editSalesOrderEvent, handleEditSalesOrder as EventListener);
      document.removeEventListener(deleteSalesOrderEvent, handleDeleteSalesOrder as EventListener);
    };
  }, [navigate]);

  // Handle search input change
  const handleSearchChange = useCallback((value: string): void => {
    setSearch(value);
    setPage(0); // Reset to first page on search
  }, [setSearch, setPage]);

  // Handle sorting change
  const handleSortingChange = useCallback((newSorting: SortingState): void => {
    setSorting(newSorting);
    setPage(0); // Reset to first page on sort change
  }, [setSorting, setPage]);

  // Handle page change
  const handlePageChange = useCallback((newPage: number): void => {
    setPage(newPage);
  }, [setPage]);

  // Handle delete confirmation
  const handleDeleteSalesOrder = (): void => {
    if (selectedSalesOrder) {
      deleteSalesOrderMutation.mutate(selectedSalesOrder.id);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Sales Orders</h1>
        <Button asChild>
          <Link to="/sales-orders/detail">
            <Plus className="h-4 w-4 mr-2" />
            New Sales Order
          </Link>
        </Button>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading sales orders: {(error as Error).message}
          </AlertDescription>
        </Alert>
      )}

      {/* Sales Orders Data Table */}
      <SalesOrderDataTable
        data={salesOrdersData?.items || []}
        columns={columns}
        isLoading={isLoading}
        pageCount={pageCount}
        page={page}
        setPage={handlePageChange}
        onSearchChange={handleSearchChange}
        searchValue={search}
        onSortingChange={handleSortingChange}
        sortingState={sorting}
        onFilterChange={handleFilterChange}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sales Order</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete sales order{' '}
              <span className="font-semibold">
                #{selectedSalesOrder?.document_number}
              </span>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSalesOrder}
              disabled={deleteSalesOrderMutation.isPending}
            >
              {deleteSalesOrderMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SalesOrdersPage;